import React, { useCallback, useEffect, useMemo, useState } from "react";
import BugReportIcon from "@mui/icons-material/BugReport";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { tePipelineDebug, getExerciseFeedback } from "./funtionsBD/Mainservice";
import {
  isTrainingTeDebugEnabled,
  trainingTeDebugLog,
} from "../utils/trainingTeDebugLog";
import "./TePipelineDebugPanel.css";

function StatusPill({ status }) {
  const label = status || "info";
  return (
    <span className={`te-pipeline-debug__pill te-pipeline-debug__pill--${label}`}>
      {label}
    </span>
  );
}

function ExerciseDebugCard({ row, selectedTeId, onSelectTeId, apiVideoCount = null }) {
  const isSelected = selectedTeId === row.te_id;
  const exec = row.resolved_execution_type || "—";

  return (
    <article
      className="te-pipeline-debug__card"
      style={isSelected ? { outline: "2px solid #0877bc" } : undefined}
    >
      <header className="te-pipeline-debug__card-head">
        <button
          type="button"
          className="te-pipeline-debug__card-title"
          style={{ border: "none", background: "none", cursor: "pointer", padding: 0 }}
          onClick={() => onSelectTeId(row.te_id)}
        >
          #{row.te_order}. {row.ex_name || row.te_id}
        </button>
        <StatusPill status={row.te_state_slug === "finalized" ? "ok" : "warn"} />
        <span>{row.te_state_slug}</span>
        <span className="te-pipeline-debug__mono">{exec}</span>
        {row.is_shared_multi_table && (
          <span className="te-pipeline-debug__pill te-pipeline-debug__pill--info">
            {row.distinct_table_count} tables
          </span>
        )}
        {row.feedback?.video_count > 0 && (
          <span className="te-pipeline-debug__pill te-pipeline-debug__pill--ok">
            {row.feedback.video_count} video(s)
          </span>
        )}
      </header>

      {(isSelected || !selectedTeId) && (
        <div className="te-pipeline-debug__card-body">
          <div className="te-pipeline-debug__grid">
            <div>
              <strong>te_id</strong>
              <div className="te-pipeline-debug__mono">{row.te_id}</div>
            </div>
            <div>
              <strong>stored type</strong>
              <div>{row.te_execution_type || "null"}</div>
            </div>
            <div>
              <strong>type</strong>
              <div>{row.type_name_en || "—"}</div>
            </div>
            <div>
              <strong>TE table_number</strong>
              <div>{row.table_number_on_te_row ?? "—"}</div>
            </div>
            <div>
              <strong>duration</strong>
              <div>{row.te_duration ?? "—"} min</div>
            </div>
            <div>
              <strong>sequences</strong>
              <div>{row.sequence_count}</div>
            </div>
            <div>
              <strong>legacy partition</strong>
              <div>{row.is_legacy_sequence_partition ? "yes" : "no"}</div>
            </div>
            <div>
              <strong>iOS eval default</strong>
              <div>{row.video_pipeline?.ios_eval_default || "—"}</div>
            </div>
          </div>

          <p style={{ margin: "0 0 0.75rem", color: "#475569" }}>
            {row.video_pipeline?.notes}
          </p>

          <h4 className="te-pipeline-debug__section-title">Pipeline checklist</h4>
          <ul className="te-pipeline-debug__checklist">
            {(row.video_pipeline?.checklist || []).map((item) => (
              <li key={item.id}>
                <StatusPill status={item.status} />
                <div>
                  <strong>{item.label}</strong>
                  <div style={{ color: "#64748b" }}>{item.detail}</div>
                </div>
              </li>
            ))}
          </ul>

          {row.sequences?.length > 0 && (
            <>
              <h4 className="te-pipeline-debug__section-title">Sequences</h4>
              <div className="te-pipeline-debug__mono">
                {row.sequences.map((s) => (
                  <div key={s.tes_id || s.sequence_order}>
                    seq {s.sequence_order}: A={s.player_a_id?.slice(-6) || "—"} (
                    {s.player_a_role}) · B={s.player_b_id?.slice(-6) || "—"} (
                    {s.player_b_role})
                    {s.table_number != null ? ` · table ${s.table_number}` : ""}
                  </div>
                ))}
              </div>
            </>
          )}

          {row.players?.length > 0 && (
            <>
              <h4 className="te-pipeline-debug__section-title">Roster (tbl_te_player)</h4>
              {row.players.map((p) => (
                <div key={`${p.player_id}-${p.table_id}`} className="te-pipeline-debug__mono">
                  T{p.table_number ?? "?"} · {p.player_position} · {p.player_name || p.player_id}
                </div>
              ))}
            </>
          )}

          <h4 className="te-pipeline-debug__section-title">
            Feedback / videos ({row.feedback?.total ?? 0})
            {apiVideoCount != null && selectedTeId === row.te_id && (
              <span style={{ fontWeight: 500, color: "#64748b" }}>
                {" "}
                · getExerciseFeedback API: {apiVideoCount} with URL
              </span>
            )}
          </h4>
          {(row.feedback?.items || []).length === 0 ? (
            <div style={{ color: "#64748b" }}>No feedback rows for this te_id</div>
          ) : (
            row.feedback.items.map((fb) => (
              <div key={fb.message_id} className="te-pipeline-debug__video-row">
                <div>
                  <strong>{fb.message_type}</strong>
                  {fb.sequence_number ? ` · seq ${fb.sequence_number}` : ""}
                  {fb.has_video ? " · has video" : ""}
                </div>
                {fb.video_message_url && (
                  <a href={fb.video_message_url} target="_blank" rel="noreferrer">
                    {fb.video_message_url}
                  </a>
                )}
                {fb.text_message && (
                  <div className="te-pipeline-debug__mono">{fb.text_message}</div>
                )}
              </div>
            ))
          )}

          {row.kot && (
            <>
              <h4 className="te-pipeline-debug__section-title">KoT matches</h4>
              <div>
                config: {row.kot.config_present ? "yes" : "no"} · matches:{" "}
                {row.kot.match_count}
              </div>
            </>
          )}
        </div>
      )}
    </article>
  );
}

export function TePipelineDebugPanel({ tsId, focusTeId = null, defaultExpanded = false }) {
  const enabled = isTrainingTeDebugEnabled();
  const [expanded, setExpanded] = useState(defaultExpanded || !!focusTeId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [selectedTeId, setSelectedTeId] = useState(focusTeId);
  const [apiVideoCount, setApiVideoCount] = useState(null);

  const loadApiFeedbackCount = useCallback(async (teId) => {
    if (!teId) {
      setApiVideoCount(null);
      return;
    }
    try {
      const res = await getExerciseFeedback({ te_id: teId });
      const rows = res?.data?.data?.feedbacks || res?.data?.feedbacks || [];
      const withUrl = rows.filter((r) => r?.video_message_url).length;
      setApiVideoCount(withUrl);
      trainingTeDebugLog("getExerciseFeedback", { te_id: teId, video_count: withUrl, total: rows.length });
    } catch (err) {
      setApiVideoCount(null);
      trainingTeDebugLog("getExerciseFeedback_error", err?.message || err);
    }
  }, []);

  const load = useCallback(async () => {
    if (!tsId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await tePipelineDebug({ ts_id: tsId });
      const data = res?.data?.data || res?.data;
      setPayload(data);
      trainingTeDebugLog("snapshot", data);
      if (focusTeId) setSelectedTeId(focusTeId);
    } catch (err) {
      setError(err?.message || "Failed to load TE pipeline debug");
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [tsId, focusTeId]);

  useEffect(() => {
    if (!enabled || !expanded || !tsId) return;
    load();
  }, [enabled, expanded, tsId, load]);

  useEffect(() => {
    if (focusTeId) setSelectedTeId(focusTeId);
  }, [focusTeId]);

  useEffect(() => {
    if (!enabled || !expanded || !selectedTeId) return;
    loadApiFeedbackCount(selectedTeId);
  }, [enabled, expanded, selectedTeId, payload, loadApiFeedbackCount]);

  const summary = useMemo(() => {
    if (!payload?.exercises?.length) return null;
    const withVideo = payload.exercises.filter((e) => e.feedback?.video_count > 0).length;
    const onceNoVideo = payload.exercises.filter(
      (e) =>
        e.resolved_execution_type === "TE_ONCE" &&
        (e.feedback?.evaluation_video_count || 0) === 0
    ).length;
    return { total: payload.exercises.length, withVideo, onceNoVideo };
  }, [payload]);

  if (!enabled) return null;

  const copyJson = async () => {
    if (!payload) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    } catch {
      /* ignore */
    }
  };

  return (
    <section className="te-pipeline-debug" aria-label="TE pipeline debug">
      <button
        type="button"
        className="te-pipeline-debug__toggle"
        onClick={() => setExpanded((v) => !v)}
      >
        <BugReportIcon fontSize="small" />
        TE pipeline debug
        {summary && (
          <span style={{ fontWeight: 500, color: "#64748b" }}>
            · {summary.withVideo}/{summary.total} with video
            {summary.onceNoVideo > 0
              ? ` · ${summary.onceNoVideo} timer TE(s) without eval clip`
              : ""}
          </span>
        )}
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </button>

      {expanded && (
        <div className="te-pipeline-debug__body">
          <div className="te-pipeline-debug__toolbar">
            <button type="button" onClick={load} disabled={loading}>
              <RefreshIcon fontSize="inherit" style={{ verticalAlign: "middle" }} /> Refresh
            </button>
            <button type="button" onClick={copyJson} disabled={!payload}>
              <ContentCopyIcon fontSize="inherit" style={{ verticalAlign: "middle" }} /> Copy JSON
            </button>
            <span className="te-pipeline-debug__mono">ts_id={tsId}</span>
          </div>

          <div className="te-pipeline-debug__meta">
            Validates execution type, roster, sequences, feedback videos, and what the saved
            results page loads via <code>getExerciseFeedback</code>. iOS timer TE logs:{" "}
            <code>saved.trace.gate</code> + <code>eval_pipeline_snapshot</code>. Enable with{" "}
            <code>?teDebug=1</code> or <code>localStorage SMARTTT_TE_DEBUG=1</code>.
          </div>

          {loading && <div>Loading pipeline snapshot…</div>}
          {error && <div className="te-pipeline-debug__error">{error}</div>}

          {payload?.exercises?.map((row) => (
            <ExerciseDebugCard
              key={row.te_id}
              row={row}
              selectedTeId={selectedTeId}
              onSelectTeId={setSelectedTeId}
              apiVideoCount={apiVideoCount}
            />
          ))}

          {payload?.legend && (
            <details style={{ marginTop: "0.5rem", fontSize: "0.8rem", color: "#64748b" }}>
              <summary>Legend</summary>
              <pre style={{ whiteSpace: "pre-wrap" }}>
                {JSON.stringify(payload.legend, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
    </section>
  );
}

export default TePipelineDebugPanel;
